import React, { Component } from "react";
import PropTypes from "prop-types";
import { evalScript } from "../../../utils/misc";
import { CCForm } from "~/components";
import { creditCardIcons } from "../../../theme/assets";
import classnames from "classnames";
import styless from "../../../components/PaymentForm/PaymentForm.scss";
import styles from "./PCIProxy.scss";
import Notification from "~/components/Notification/Notification";
import { defineMessages, FormattedMessage } from "react-intl";
import constants from "../../../utils/constants";

const msgs = defineMessages({
  paymentErrorMessage: {
    id: "payNoPainErrorMessage.genericError",
    description: "Any other error occured",
    defaultMessage: "The transaction could not be completed.",
  },
  tryAgain: {
    id: "payNoPainErrorMessage.tryAgain",
    description: "Try again message",
    defaultMessage: "Please Try Again.",
  },
});

/*
 * How it works
 * -User clicks on ConfirmReservation
 * -Reservation is created on the backend, client gets success answer
 * -Application detects current device (mobile or desktop)
 *   if desktop opens new browser windows with THIS component (special route just for it (a.k.a. WITBOOKING_PAYMENT_ROUTE))
 *   if mobile redirects current window to THIS component
 * -[COMPONENT FLOW A] Component redirects to WitbookingPaymentSystem (a.k.a. PAYMENT_ROUTE)
 *   We must pass Locale / Current Domain / Reservation ID / propertyTicker / Environment(dev, prod)?
 * -User fills CC data in WitbookingPaymentSystem
 * -WitbookingPaymentSystem redirect to either our success or fail state
 * -[COMPONENT FLOW B] If success
 *   if on desktop the browser window is closed (check for window.opener) and the parent is redirected to the confirmation page
 *   if on mobile the (window.opener is null) the component redirect to the confirmation page
 * -[COMPONENT FLOW C] If fail (how?) the user is shown an error message with a button to try again which redirects to WITBOOKING_PAYMENT_ROUTE and start over.
 */
function CreditCardIcons({ creditCardTypes }) {
  return (
    <div className={classnames("wbkv9-OnlinePayment-creditCardIcons", styless.creditCardIcons)}>
      {creditCardTypes.map((creditCard, idx) => (
        <div
          key={idx}
          className={classnames(
            "wbkv9-OnlinePayment-creditCardIconContainer",
            styless.creditCardIconContainer
          )}>
          <img src={creditCardIcons[creditCard]} alt={creditCard} />
        </div>
      ))}
    </div>
  );
}
const proxyStyles = {
  "*": {
    height: "35px",
    padding: "5px 8px",
    width: "100%",
    border: "none",
    "font-family": "'Open Sans', sans-serif",
    fontSize: "14px",
  },
  "*::placeholder": "color: #bdbdbd",
  "@font-face": {
    "*": {
      fontFamily: "Open Sans",
      fontStyle: "normal",
      fontWeight: 400,
      src: "url('OpenSans-Regular.ttf') format('ttf')",
    },
  },
};

export function submit() {
  if (window && window.Inline) {
    window.Inline.submit();
  } else {
    console.error("PCI Proxy Not initialized");
  }
}

class PCIProxy extends Component {
  constructor(props) {
    super(props);
    this.initialized = false;
    this.processed = false;
    this.initialize = this.initialize.bind(this);
    this.handlePaymentResponse = this.handlePaymentResponse.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.transactionId = null;

    const scriptURL =
      window.__additionalData.PCI_PROXY_SCRIPT_URL ||
      "https://pay.datatrans.com/upp/payment/js/datatrans-inline-1.0.0.js";

    if (window && !window.Inline) {
      evalScript({
        scriptArr: [{ src: scriptURL }],
        cb: result => {
          if (result) {
            this.initialize();
          }
        },
      });
    }
  }

  componentDidMount() {
    if (window && window.Inline) {
      this.initialize();
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.pciProxyError !== this.props.pciProxyError && this.props.pciProxyError) {
      this.processed = false;
    }
    if (
      !this.processed &&
      this.props.idReservation &&
      this.props.idReservation !== prevProps.idReservation
    ) {
      if (this.transactionId) {
        console.log(
          `Both idreservation ${this.props.idReservation} and transaction id ${this.transactionId} are present`
        );
        this.processed = true;
        this.props.handlePCIProxySubmit({
          reservationId: this.props.idReservation,
          transactionId: this.transactionId,
        });
      }
    }
  }

  initialize() {
    if (!this.initialized) {
      const fields = {
        cardNumber: {
          placeholderElementId: "pciProxy-wbkv9-cc-number",
          placeholder: "Número tarjeta",
        },
      };
      if (this.props.fields && this.props.fields.hasOwnProperty("securityCode")) {
        fields.cvv = {
          placeholderElementId: "pciProxy-wbkv9-cvc-number",
          placeholder: "CVC",
        };
      }

      const pciCode = window.__additionalData.PCI_PROXY_CODE || "3000013070";

      window.Inline.initTokenize(pciCode, fields, {
        styles: proxyStyles,
      });

      window.Inline.on("success", data => {
        if (data.transactionId) {
          console.log("received pci proxy data");
          console.log(data);
          this.transactionId = data.transactionId;
          if (!this.processed && this.props.idReservation) {
            console.log(
              `Both idreservation ${this.props.idReservation} and transaction id ${this.transactionId} are present`
            );
            this.props.handlePCIProxySubmit({
              reservationId: this.props.idReservation,
              transactionId: this.transactionId,
            });
            this.processed = true;
          }
        } else {
          console.log("PCI PROXY ERROR");
          console.log(data);
          this.props.createPCIProxyError("PCI Proxy Error");
        }
      });
      window.Inline.on("error", data => {
        if (data) {
          this.props.createPCIProxyError("PCI Proxy Error");
        }
      });

      window.Inline.on("change", event => {
        if (!event) {
          return;
        }
        const { fields: { cardNumber, cvv } = {}, hasErrors, event: onChangeEvent } = event; // eslint-disable-line
        if (onChangeEvent) {
          if (onChangeEvent.field === "cardNumber") {
            if (this.props.fields.ccNumber.valid !== cardNumber.valid) {
              this.props.fields.ccNumber.onChange(cardNumber.valid ? "4111111111111111" : false);
            }
          }
          if (onChangeEvent.field === "cvv") {
            if (this.props.fields.securityCode.valid !== cvv.valid) {
              this.props.fields.securityCode.onChange(cvv.valid ? "123" : false);
            }
          }
        }
      });

      this.initialized = true;
    }
  }

  handlePaymentResponse(token, args) {
    this.props.onResponse({ token, args });
  }

  handleClose() {
    this.props.onClose();
  }

  render() {
    const {
      fields,
      creditCardTypes,
      depositTotal,
      pciProxyError,
      propertyTicker,
      description,
    } = this.props;
    return (
      <div className={classnames("wbkv9-pci-proxy", styles.PCIProxy)}>
        <div>
          {pciProxyError ? (
            <Notification
              id={"9999"}
              position={constants.MESSAGE_POSITIONS.TOP_METHOD_PAYMENT_STEP_2}
              text={
                <span>
                  <FormattedMessage {...msgs.paymentErrorMessage} />
                  {". "}
                  <FormattedMessage {...msgs.tryAgain} />
                </span>
              }
              type={"error"}
              propertyTicker={propertyTicker}
              hideIcon
            />
          ) : (
            false
          )}
        </div>
        <CCForm
          fields={fields}
          paymentType="pciProxy"
          creditCardTypes={creditCardTypes}
          depositTotal={depositTotal}
          description={description}
          creditCardsList={<CreditCardIcons creditCardTypes={creditCardTypes} />}
        />
      </div>
    );
  }
}
PCIProxy.propTypes = {
  fields: PropTypes.object,
  creditCardTypes: PropTypes.object,
  depositTotal: PropTypes.number,
  onInitialize: PropTypes.func,
  onResponse: PropTypes.func,
  onClose: PropTypes.func,
  handlePCIProxySubmit: PropTypes.func,
  onPayment: PropTypes.func,
  configuration: PropTypes.object,
  transaction: PropTypes.object,
  idReservation: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  pciProxyError: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  propertyTicker: PropTypes.string,
  description: PropTypes.object,
  createPCIProxyError: PropTypes.func,
};

export default PCIProxy;
